import React, { useState } from 'react';
import { PlayIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon, DownloadIcon, ClockIcon, ZapIcon, SearchIcon } from 'lucide-react';
import { Button } from '../../ui/Button';
import { runPipeline } from '../../../governance/pipeline/orchestrator';
import { ALL_QUESTIONS_CORE } from '../../../data/questionLoader';
type Severity = 'BLOCKER' | 'WARNING' | 'INFO';
type Stage = 'schema' | 'type' | 'content' | 'support' | 'visual' | 'graph';
type RunStatus = 'idle' | 'running' | 'complete';
interface ValidationIssue {
  id: string;
  questionId: string;
  stage: Stage;
  severity: Severity;
  ruleId: string;
  message: string;
  field?: string;
  suggestion?: string;
}
interface ValidationResult {
  questionId: string;
  status: 'pass' | 'blocked' | 'warning';
  issues: ValidationIssue[];
  score: number;
}
// ─── Domain → Stage mapping ───────────────────────────────────────────────────
const DOMAIN_TO_STAGE: Record<string, Stage> = {
  schema: 'schema',
  type: 'type',
  graph: 'graph',
  content: 'content',
  visual: 'visual',
  support: 'support',
  scoring: 'content',
  gate: 'schema'
};
// ─── Pipeline result mapper ───────────────────────────────────────────────────
function mapPipelineResult(pipelineResult: ReturnType<typeof runPipeline>): ValidationResult {
  const {
    gate,
    questionId
  } = pipelineResult;
  const status: ValidationResult['status'] = gate.verdict === 'PASS' ? 'pass' : gate.verdict === 'REVIEW_NEEDED' ? 'warning' : 'blocked';
  const issues: ValidationIssue[] = gate.issues.map((issue, idx) => ({
    id: `${questionId}-${idx}`,
    questionId,
    stage: DOMAIN_TO_STAGE[issue.domain] ?? 'content',
    severity: issue.kind === 'blocker' ? 'BLOCKER' : issue.kind === 'coach' ? 'WARNING' : 'INFO',
    ruleId: issue.code,
    message: issue.message,
    field: issue.field,
    suggestion: issue.suggestion
  }));
  return {
    questionId,
    status,
    issues,
    score: gate.score
  };
}
const STAGE_LABELS: Record<Stage, string> = {
  schema: 'Schema',
  type: 'Vraagtype',
  content: 'Inhoud',
  support: 'Ondersteuning',
  visual: 'Visueel',
  graph: 'LDG-Graaf'
};
const SEVERITY_CONFIG: Record<Severity, {
  icon: React.ReactNode;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  BLOCKER: {
    icon: <XCircleIcon className="h-4 w-4" data-id="element-3704" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Blokkerend'
  },
  WARNING: {
    icon: <AlertTriangleIcon className="h-4 w-4" data-id="element-3705" />,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Waarschuwing'
  },
  INFO: {
    icon: <InfoIcon className="h-4 w-4" data-id="element-3706" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Informatief'
  }
};
function IssueRow({
  issue
}: {
  issue: ValidationIssue;
}) {
  const sev = SEVERITY_CONFIG[issue.severity];
  return <div className={`rounded-lg border ${sev.border} ${sev.bg} p-3`} data-id="element-3707">
      <div className="flex items-start gap-2" data-id="element-3708">
        <span className={`flex-shrink-0 mt-0.5 ${sev.text}`} data-id="element-3709">{sev.icon}</span>
        <div className="min-w-0 flex-1" data-id="element-3710">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-3711">
            <code className="text-xs text-gray-400 font-mono" data-id="element-3712">
              {issue.ruleId}
            </code>
            <span className={`text-xs font-semibold ${sev.text}`} data-id="element-3713">
              {sev.label}
            </span>
            <span className="text-xs text-gray-500 bg-white/70 rounded px-1.5 py-0.5 border border-gray-200" data-id="element-3714">
              {STAGE_LABELS[issue.stage]}
            </span>
            {issue.field && <code className="text-xs text-gray-400 bg-white/70 rounded px-1.5 py-0.5 border border-gray-200" data-id="element-3715">
                .{issue.field}
              </code>}
          </div>
          <p className="text-sm text-gray-700" data-id="element-3716">{issue.message}</p>
          {issue.suggestion && <p className="text-xs text-gray-500 mt-1" data-id="element-3717">
              <span className="font-medium" data-id="element-3718">Oplossing:</span> {issue.suggestion}
            </p>}
        </div>
      </div>
    </div>;
}
function ResultRow({
  result
}: {
  result: ValidationResult;
}) {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = {
    pass: {
      icon: <CheckCircleIcon className="h-4 w-4 text-emerald-500" data-id="element-3719" />,
      text: 'text-emerald-700',
      bg: 'bg-emerald-50',
      label: 'Geslaagd'
    },
    blocked: {
      icon: <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-3720" />,
      text: 'text-red-700',
      bg: 'bg-red-50',
      label: 'Geblokkeerd'
    },
    warning: {
      icon: <AlertTriangleIcon className="h-4 w-4 text-amber-500" data-id="element-3721" />,
      text: 'text-amber-700',
      bg: 'bg-amber-50',
      label: 'Waarschuwing'
    }
  };
  const cfg = statusConfig[result.status];
  const scoreColor = result.score >= 90 ? 'text-emerald-600' : result.score >= 70 ? 'text-amber-600' : 'text-red-600';
  return <div className="rounded-lg border border-gray-100 bg-white overflow-hidden" data-id="element-3722">
      <button className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50/50 transition-colors" onClick={() => result.issues.length > 0 && setExpanded(!expanded)} disabled={result.issues.length === 0} data-id="element-3723">
        <span className="flex-shrink-0" data-id="element-3724">{cfg.icon}</span>
        <div className="min-w-0 flex-1" data-id="element-3725">
          <code className="text-sm font-mono text-gray-700" data-id="element-3726">
            {result.questionId}
          </code>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0" data-id="element-3727">
          <span className={`text-sm font-bold ${scoreColor}`} data-id="element-3728">
            {result.score}%
          </span>
          {result.issues.length > 0 && <span className="text-xs text-gray-500" data-id="element-3729">
              {result.issues.length} issue{result.issues.length > 1 ? 's' : ''}
            </span>}
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cfg.bg} ${cfg.text}`} data-id="element-3730">
            {cfg.label}
          </span>
          {result.issues.length > 0 && <span className="text-gray-400" data-id="element-3731">
              {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3732" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3733" />}
            </span>}
        </div>
      </button>
      {expanded && result.issues.length > 0 && <div className="border-t border-gray-100 px-4 py-3 space-y-2 bg-gray-50/50" data-id="element-3734">
          {result.issues.map(issue => <IssueRow key={issue.id} issue={issue} data-id="element-3735" />)}
        </div>}
    </div>;
}
export function GovernanceValidationTab() {
  const [runStatus, setRunStatus] = useState<RunStatus>('idle');
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [progress, setProgress] = useState(0);
  const [filter, setFilter] = useState<'all' | 'blocked' | 'warning' | 'pass'>('all');
  const [search, setSearch] = useState('');
  const [selectedScope, setSelectedScope] = useState<string>('all');
  const [runDuration, setRunDuration] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<string>('');
  const handleRun = () => {
    setRunStatus('running');
    setProgress(0);
    setResults([]);
    setCurrentStage('Schema laden...');
    const startTime = Date.now();
    // Filter questions by scope
    const questions = selectedScope === 'all' ? ALL_QUESTIONS_CORE : ALL_QUESTIONS_CORE.filter(q => q.id.startsWith(selectedScope));
    const BATCH_SIZE = 20;
    let processed = 0;
    const allResults: ValidationResult[] = [];
    const stages = ['Schema...', 'Type...', 'Inhoud...', 'Visueel...', 'Ondersteuning...', 'LDG-Graaf...'];
    const processBatch = () => {
      const batch = questions.slice(processed, processed + BATCH_SIZE);
      for (const question of batch) {
        try {
          const pipelineResult = runPipeline(question);
          allResults.push(mapPipelineResult(pipelineResult));
        } catch {
          // Skip questions that cause pipeline errors
        }
        processed++;
      }
      const pct = Math.round(processed / Math.max(questions.length, 1) * 100);
      setProgress(pct);
      setCurrentStage(stages[Math.floor(pct / 100 * stages.length)] ?? 'Afronden...');
      if (processed < questions.length) {
        setTimeout(processBatch, 0);
      } else {
        setRunStatus('complete');
        setResults(allResults);
        setRunDuration(`${((Date.now() - startTime) / 1000).toFixed(1)}s`);
        setCurrentStage('');
      }
    };
    // Start processing after a tick so the UI can update
    setTimeout(processBatch, 50);
  };
  const filteredResults = results.filter(r => {
    const matchesFilter = filter === 'all' || r.status === filter;
    const matchesSearch = search === '' || r.questionId.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  const blockedCount = results.filter(r => r.status === 'blocked').length;
  const warningCount = results.filter(r => r.status === 'warning').length;
  const passCount = results.filter(r => r.status === 'pass').length;
  const avgScore = results.length > 0 ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / results.length) : 0;
  const scopes = [{
    value: 'all',
    label: 'Alle vragen'
  }, {
    value: 'GET',
    label: 'Getallen (GET)'
  }, {
    value: 'MET',
    label: 'Meten (MET)'
  }, {
    value: 'SPR',
    label: 'Spelling (SPR)'
  }, {
    value: 'TAA',
    label: 'Taal (TAA)'
  }, {
    value: 'TGL',
    label: 'Tijd & Geld (TGL)'
  }, {
    value: 'TOPO',
    label: 'Topografie (TOPO)'
  }, {
    value: 'SWW',
    label: 'Werkwoorden (SWW)'
  }, {
    value: 'STU',
    label: 'Studievaardigheden (STU)'
  }];
  return <div className="space-y-5" data-id="element-3736">
      {/* Run Panel */}
      <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3737">
        <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3738">
          Validatie Uitvoeren
        </h2>
        <p className="text-sm text-gray-500 mb-4" data-id="element-3739">
          Voer de volledige governance-pipeline uit op een selectie van vragen.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end" data-id="element-3740">
          <div className="flex-1" data-id="element-3741">
            <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-3742">
              Scope
            </label>
            <select value={selectedScope} onChange={e => setSelectedScope(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" disabled={runStatus === 'running'} data-id="element-3743">
              {scopes.map(s => <option key={s.value} value={s.value} data-id="element-3744">
                  {s.label}
                </option>)}
            </select>
          </div>
          <Button variant="primary" onClick={handleRun} disabled={runStatus === 'running'} data-id="element-3745">
            {runStatus === 'running' ? <span className="flex items-center gap-2" data-id="element-3746">
                <RefreshCwIcon className="h-4 w-4 animate-spin" data-id="element-3747" />
                Valideren...
              </span> : <span className="flex items-center gap-2" data-id="element-3748">
                <PlayIcon className="h-4 w-4" data-id="element-3749" />
                Validatie Starten
              </span>}
          </Button>
        </div>

        {/* Progress Bar */}
        {runStatus === 'running' && <div className="mt-4" data-id="element-3750">
            <div className="flex items-center justify-between mb-1" data-id="element-3751">
              <span className="text-xs text-gray-500" data-id="element-3752">{currentStage}</span>
              <span className="text-xs font-medium text-blue-600" data-id="element-3753">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-100 overflow-hidden" data-id="element-3754">
              <div className="h-full rounded-full bg-blue-500 transition-all duration-300" style={{
            width: `${progress}%`
          }} data-id="element-3755" />
            </div>
          </div>}
      </div>

      {/* Results */}
      {runStatus === 'complete' && results.length > 0 && <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-3756">
            <button onClick={() => setFilter('all')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'all' ? 'border-gray-300 bg-gray-50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`} data-id="element-3757">
              <p className="text-2xl font-bold text-gray-800" data-id="element-3758">
                {results.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-3759">Totaal</p>
            </button>
            <button onClick={() => setFilter('pass')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'pass' ? 'border-emerald-300 bg-emerald-50 shadow-sm' : 'border-gray-100 bg-white hover:border-emerald-200'}`} data-id="element-3760">
              <p className="text-2xl font-bold text-emerald-700" data-id="element-3761">{passCount}</p>
              <p className="text-xs text-emerald-600" data-id="element-3762">Geslaagd</p>
            </button>
            <button onClick={() => setFilter('warning')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'warning' ? 'border-amber-300 bg-amber-50 shadow-sm' : 'border-gray-100 bg-white hover:border-amber-200'}`} data-id="element-3763">
              <p className="text-2xl font-bold text-amber-700" data-id="element-3764">
                {warningCount}
              </p>
              <p className="text-xs text-amber-600" data-id="element-3765">Waarschuwingen</p>
            </button>
            <button onClick={() => setFilter('blocked')} className={`rounded-xl border p-3 text-center transition-all ${filter === 'blocked' ? 'border-red-300 bg-red-50 shadow-sm' : 'border-gray-100 bg-white hover:border-red-200'}`} data-id="element-3766">
              <p className="text-2xl font-bold text-red-700" data-id="element-3767">{blockedCount}</p>
              <p className="text-xs text-red-600" data-id="element-3768">Geblokkeerd</p>
            </button>
          </div>

          {/* Meta info */}
          <div className="flex items-center justify-between" data-id="element-3769">
            <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-3770">
              <span className="flex items-center gap-1" data-id="element-3771">
                <ClockIcon className="h-4 w-4" data-id="element-3772" />
                Duur: {runDuration}
              </span>
              <span className="flex items-center gap-1" data-id="element-3773">
                <ZapIcon className="h-4 w-4" data-id="element-3774" />
                Gem. score:{' '}
                <strong className="text-gray-700 ml-1" data-id="element-3775">{avgScore}%</strong>
              </span>
            </div>
            <Button variant="secondary" size="sm" data-id="element-3776">
              <DownloadIcon className="h-4 w-4 mr-1.5" data-id="element-3777" />
              Exporteer Rapport
            </Button>
          </div>

          {/* Search */}
          <div className="relative" data-id="element-3778">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-3779" />
            <input type="text" placeholder="Zoek op vraag-ID..." value={search} onChange={e => setSearch(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-3780" />
          </div>

          {/* Results List */}
          <div className="space-y-2" data-id="element-3781">
            {filteredResults.length === 0 ? <div className="rounded-lg border border-gray-100 bg-gray-50 py-10 text-center" data-id="element-3782">
                <p className="text-gray-500 text-sm" data-id="element-3783">
                  Geen resultaten voor deze filter.
                </p>
              </div> : filteredResults.map(result => <ResultRow key={result.questionId} result={result} data-id="element-3784" />)}
          </div>
        </>}

      {/* Idle State */}
      {runStatus === 'idle' && <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center" data-id="element-3785">
          <ZapIcon className="mx-auto h-10 w-10 text-gray-300 mb-3" data-id="element-3786" />
          <p className="text-gray-500 font-medium" data-id="element-3787">
            Nog geen validatie uitgevoerd
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-3788">
            Klik op "Validatie Starten" om de pipeline uit te voeren.
          </p>
        </div>}
    </div>;
}